import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { runCartMutation, runCartRequest } from '@theme/skin-elite-ebook-gift';

export const resolveBalmSubscription = ({ checked, config, variantId, quantity, oneTimeTotalCents }) => {
  if (!checked) return { active: false };

  const unavailable = { active: false, error: 'Monthly delivery is unavailable for this selection. Untick subscription to order once.' };
  const planId = String(config?.planId || '');
  const totalCents = Number(config?.totals?.[String(quantity)]);
  const oneJarTotalCents = Number(config?.totals?.['1']);
  const allocation = (Array.isArray(config?.allocations) ? config.allocations : []).find((item) => (
    String(item?.variantId) === String(variantId) && String(item?.planId) === planId
  ));

  if (!config?.enabled || !config.backendVerified || config.currency !== 'USD'
    || !/^\d+$/.test(planId) || !/^\d+$/.test(String(variantId))
    || ![1, 4, 6].includes(quantity) || !allocation
    || !Number.isSafeInteger(totalCents) || totalCents <= 0
    || !Number.isSafeInteger(oneJarTotalCents) || oneJarTotalCents <= 0
    || Number(allocation.unitPriceCents) !== oneJarTotalCents
    || !Number.isSafeInteger(oneTimeTotalCents) || oneTimeTotalCents <= 0
    || totalCents !== Math.round(oneTimeTotalCents * 0.85)) return unavailable;

  return { active: true, sellingPlanId: planId, totalCents, quantity };
};

export const buildBalmCartItem = (variantId, quantity, subscription = { active: false }) => {
  if (subscription.error) throw new Error(subscription.error);
  const item = { id: variantId, quantity };
  if (subscription.active) {
    if (!/^\d+$/.test(String(subscription.sellingPlanId)) || subscription.quantity !== quantity) {
      throw new Error('The monthly selection could not be validated. Please select your order again.');
    }
    item.selling_plan = subscription.sellingPlanId;
  }
  return item;
};

(() => {
  const balmDrawerClass = 'skin-elite-balm-cart-drawer';
  const subscriptionConfigs = new WeakMap();

  const formatMoney = (cents, currencySymbol = '$') => {
    const value = Number(cents || 0) / 100;
    return `${currencySymbol}${value.toFixed(2)}`;
  };

  const readSubscriptionConfig = (root) => {
    if (subscriptionConfigs.has(root)) return subscriptionConfigs.get(root);
    let config = null;
    try {
      const source = root.querySelector('[data-balm-subscription-config]');
      if (source) config = JSON.parse(source.textContent);
    } catch {
      // Missing or malformed configuration never enables a subscription purchase.
    }
    subscriptionConfigs.set(root, config);
    return config;
  };

  const oneTimeBundleTotal = (root, quantity) => {
    const select = root.querySelector('[data-variant-select]');
    const cents = Number(select?.selectedOptions[0]?.dataset.price || root.dataset.unitPriceCents || 0);
    const radio = [...root.querySelectorAll('[data-bundle-quantity]')]
      .find((option) => Number(option.dataset.bundleQuantity) === quantity);
    const total = radio?.closest('.ab-bundle')?.querySelector('[data-bundle-total]');
    const fixedCents = Number(total?.dataset.fixedTotalCents || 0);
    return fixedCents > 0 ? fixedCents : cents * Number(total?.dataset.paidMultiplier || 1);
  };

  const subscriptionForSelection = (root, variantId, quantity) => resolveBalmSubscription({
    checked: root.querySelector('[data-balm-subscription-checkbox]')?.checked === true,
    config: readSubscriptionConfig(root),
    variantId,
    quantity,
    oneTimeTotalCents: oneTimeBundleTotal(root, quantity),
  });

  const updateSubscriptionOffer = (root) => {
    const checkbox = root.querySelector('[data-balm-subscription-checkbox]');
    if (!checkbox) return;

    const quantity = Number(root.querySelector('[data-bundle-quantity]:checked')?.dataset.bundleQuantity || 1);
    const variantId = root.querySelector('.ab-product-form [name="id"]')?.value || '';
    const state = subscriptionForSelection(root, variantId, quantity);
    const planInput = root.querySelector('[data-balm-selling-plan]');
    const terms = root.querySelector('[data-balm-subscription-terms]');
    const purchaseLabel = root.querySelector('[data-balm-purchase-label]');
    const purchaseSubtitle = root.querySelector('[data-balm-purchase-subtitle]');
    const symbol = root.dataset.moneySymbol || '$';

    if (planInput) {
      planInput.disabled = !state.active;
      planInput.value = state.active ? state.sellingPlanId : '';
    }
    root.dataset.balmSubscriptionActive = String(state.active);

    root.querySelectorAll('[data-balm-gift-perk]').forEach((perk) => {
      if (!perk.dataset.oneTimeText) perk.dataset.oneTimeText = perk.textContent;
      perk.textContent = state.active
        ? `${perk.dataset.oneTimeText} — First order only`
        : perk.dataset.oneTimeText;
    });

    if (purchaseLabel) purchaseLabel.textContent = state.active ? 'Monthly Order' : purchaseLabel.dataset.oneTimeLabel;
    if (purchaseSubtitle) purchaseSubtitle.textContent = state.active ? 'Cancel anytime' : purchaseSubtitle.dataset.oneTimeSubtitle;
    if (terms) {
      terms.classList.toggle('is-error', Boolean(state.error));
      terms.textContent = state.error || (state.active
        ? `${quantity} ${quantity === 1 ? 'jar' : 'jars'} delivered every month. ${quantity === 1 ? `Recurring total: ${formatMoney(state.totalCents + 495, symbol)} per month (${formatMoney(state.totalCents, symbol)} product + $4.95 shipping), excluding taxes.` : `Recurring total: ${formatMoney(state.totalCents, symbol)} per month, excluding taxes. Free shipping.`}${quantity >= 3 ? ' Gifts shown above are included with your first order only and do not repeat.' : ''}`
        : 'Select monthly delivery to see your recurring order total.');
    }

    if (!state.active) return;
    root.querySelectorAll('[data-bundle-total]').forEach((price) => {
      const bundleQuantity = Number(price.closest('.ab-bundle')?.querySelector('[data-bundle-quantity]')?.dataset.bundleQuantity);
      const bundleState = subscriptionForSelection(root, variantId, bundleQuantity);
      if (bundleState.active) price.textContent = formatMoney(bundleState.totalCents, symbol);
    });
    const unitPrice = root.querySelector('[data-unit-price]');
    const oneJarState = subscriptionForSelection(root, variantId, 1);
    if (unitPrice && oneJarState.active) unitPrice.textContent = formatMoney(oneJarState.totalCents, symbol);
    root.querySelectorAll('[data-bundle-each]').forEach((price) => {
      const bundleQuantity = Number(price.dataset.totalQuantity || 1);
      const bundleState = subscriptionForSelection(root, variantId, bundleQuantity);
      if (bundleState.active) price.textContent = formatMoney(bundleState.totalCents / bundleQuantity, symbol);
    });
  };

  const initThemeCompatibility = () => {
    const suppressScratchPopup = () => {
      const popup = document.querySelector('.skin-elite-scratch-popup');
      if (popup) {
        if (!popup.hidden) popup.hidden = true;
        if (popup.getAttribute('aria-hidden') !== 'true') popup.setAttribute('aria-hidden', 'true');
      }
      if (document.body.classList.contains('skin-elite-scratch-popup-open')) {
        document.body.classList.remove('skin-elite-scratch-popup-open');
      }
    };

    suppressScratchPopup();
    const observer = new MutationObserver(suppressScratchPopup);
    observer.observe(document.body, { attributes: true, childList: true });
  };

  const initGallery = (root) => {
    const mainImage = root.querySelector('[data-balm-main-image]');
    const thumbnailRail = root.querySelector('[data-balm-thumbnails]');
    const previousButton = root.querySelector('[data-balm-gallery-prev]');
    const nextButton = root.querySelector('[data-balm-gallery-next]');
    const thumbnails = Array.from(root.querySelectorAll('[data-balm-thumbnail]'));

    const updateGalleryNavigation = () => {
      if (!thumbnailRail) return;
      const maxScrollLeft = Math.max(0, thumbnailRail.scrollWidth - thumbnailRail.clientWidth);
      if (previousButton) previousButton.disabled = thumbnailRail.scrollLeft <= 2;
      if (nextButton) nextButton.disabled = thumbnailRail.scrollLeft >= maxScrollLeft - 2;
    };

    const scrollThumbnails = (direction) => {
      if (!thumbnailRail) return;
      const firstThumbnail = thumbnails[0];
      const railStyles = window.getComputedStyle(thumbnailRail);
      const gap = Number.parseFloat(railStyles.columnGap || railStyles.gap) || 0;
      const itemWidth = firstThumbnail?.getBoundingClientRect().width || (thumbnailRail.clientWidth / 5);
      const distance = Math.max((itemWidth + gap) * 3, 180);
      thumbnailRail.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    previousButton?.addEventListener('click', () => scrollThumbnails(-1));
    nextButton?.addEventListener('click', () => scrollThumbnails(1));
    thumbnailRail?.addEventListener('scroll', updateGalleryNavigation, { passive: true });
    window.addEventListener('resize', updateGalleryNavigation, { passive: true });

    thumbnails.forEach((button) => {
      button.addEventListener('click', () => {
        if (!mainImage) return;

        mainImage.style.opacity = '0.25';
        window.setTimeout(() => {
          mainImage.src = button.dataset.imageSrc;
          mainImage.srcset = '';
          mainImage.alt = button.dataset.imageAlt || mainImage.alt;
          mainImage.style.opacity = '1';
        }, 120);

        thumbnails.forEach((thumbnail) => {
          thumbnail.classList.toggle('is-active', thumbnail === button);
        });

        if (thumbnailRail) {
          const centeredLeft = button.offsetLeft - ((thumbnailRail.clientWidth - button.offsetWidth) / 2);
          thumbnailRail.scrollTo({ left: Math.max(0, centeredLeft), behavior: 'smooth' });
        }
      });
    });

    updateGalleryNavigation();
  };

  const initOffers = (root) => {
    const quantityInput = root.querySelector('[data-quantity-input]');
    const variantSelect = root.querySelector('[data-variant-select]');
    const unitPrice = root.querySelector('[data-unit-price]');
    const submitButton = root.querySelector('[data-primary-cta]');

    const updateBundlePrices = () => {
      const option = variantSelect?.selectedOptions[0];
      const cents = Number(option?.dataset.price || root.dataset.unitPriceCents || 0);
      const formatted = option?.dataset.priceFormatted || formatMoney(cents, root.dataset.moneySymbol || '$');
      const symbol = formatted.trim().charAt(0) || '$';

      if (unitPrice && formatted) unitPrice.textContent = formatted;
      root.querySelectorAll('[data-bundle-total]').forEach((price) => {
        const multiplier = Number(price.dataset.paidMultiplier || 1);
        const fixedTotalCents = Number(price.dataset.fixedTotalCents || 0);
        const totalCents = fixedTotalCents > 0 ? fixedTotalCents : cents * multiplier;
        price.textContent = formatMoney(totalCents, symbol);
      });
      root.querySelectorAll('[data-bundle-compare]').forEach((price) => {
        const multiplier = Number(price.dataset.totalMultiplier || 1);
        const fixedCompareCents = Number(price.dataset.fixedCompareCents || 0);
        const compareCents = fixedCompareCents > 0 ? fixedCompareCents : cents * multiplier;
        price.textContent = formatMoney(compareCents, symbol);
      });
      root.querySelectorAll('[data-bundle-each]').forEach((price) => {
        const paidMultiplier = Number(price.dataset.paidMultiplier || 1);
        const totalQuantity = Number(price.dataset.totalQuantity || 1);
        const fixedTotalCents = Number(price.dataset.fixedTotalCents || 0);
        const totalCents = fixedTotalCents > 0 ? fixedTotalCents : cents * paidMultiplier;
        price.textContent = formatMoney(totalCents / totalQuantity, symbol);
      });

      updateSubscriptionOffer(root);
      updateSelectedOffer();
    };

    const updateSelectedOffer = () => {
      const selected = root.querySelector('[data-bundle-quantity]:checked');
      if (!selected) return;

      const selectedCard = selected.closest('.ab-bundle');
      const total = selectedCard?.querySelector('[data-bundle-total]')?.textContent?.trim();
      const baseLabel = submitButton?.dataset.ctaBase || 'Add to cart';
      const ctaLabel = total ? `${baseLabel} | ${total}` : baseLabel;

      if (quantityInput) quantityInput.value = selected.dataset.bundleQuantity;
      if (submitButton) {
        submitButton.dataset.currentLabel = ctaLabel;
        if (submitButton.getAttribute('aria-busy') !== 'true') submitButton.textContent = ctaLabel;
      }

      root.querySelectorAll('.ab-bundle').forEach((bundle) => {
        const isSelected = bundle === selectedCard;
        bundle.classList.toggle('is-selected', isSelected);
        bundle.setAttribute('aria-selected', String(isSelected));
      });
    };

    root.querySelectorAll('[data-bundle-quantity]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        updateBundlePrices();
      });
    });

    updateBundlePrices();
    variantSelect?.addEventListener('change', updateBundlePrices);
    const subscriptionCheckbox = root.querySelector('[data-balm-subscription-checkbox]');
    if (subscriptionCheckbox && readSubscriptionConfig(root)) {
      subscriptionCheckbox.disabled = false;
      subscriptionCheckbox.addEventListener('change', updateBundlePrices);
    }
  };

  const cartRoutes = () => {
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const root = window.Shopify?.routes?.root || '/';
    return {
      add: routes?.cart_add_url || `${root}cart/add.js`,
      cart: `${routes?.cart_url || `${root}cart`}.js`,
      page: routes?.cart_url || `${root}cart`,
    };
  };

  const fetchCart = () => runCartRequest(async (signal) => {
    const response = await fetch(cartRoutes().cart, {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Your cart could not be refreshed.');
    return response.json();
  });

  const initCartDrawer = (root) => {
    const form = root.querySelector('.ab-product-form');
    const submitButton = form?.querySelector('[data-primary-cta]');
    const status = form?.querySelector('[data-balm-cart-status]');
    if (!form || !submitButton) return;

    let busy = false;
    let cartNeedsReview = false;
    const originalLabel = submitButton.textContent.trim();
    const currentLabel = () => submitButton.dataset.currentLabel || originalLabel;

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', isError);
      status.setAttribute('role', isError ? 'alert' : 'status');
    };

    const setButtonState = () => {
      submitButton.disabled = busy || cartNeedsReview;
      submitButton.setAttribute('aria-busy', String(busy));
      submitButton.textContent = busy ? 'Adding…' : cartNeedsReview ? 'Check your cart' : currentLabel();
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || cartNeedsReview) return;

      const formData = new FormData(form);
      const variantId = String(formData.get('id') || '');
      const quantity = Number(formData.get('quantity')) || 1;
      if (!/^\d+$/.test(variantId) || quantity < 1) {
        setStatus('This selection is not available. Please refresh the page.', true);
        return;
      }
      const subscription = subscriptionForSelection(root, variantId, quantity);
      if (subscription.error) {
        setStatus(subscription.error, true);
        return;
      }
      const cartItem = buildBalmCartItem(variantId, quantity, subscription);

      busy = true;
      setButtonState();
      setStatus('Adding your selection to cart…');

      document.getElementById('cart-drawer')?.classList.add(balmDrawerClass);

      const sections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((component) => component.dataset.sectionId)
        .filter((id, index, ids) => id && ids.indexOf(id) === index);
      const deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      const detail = {
        source: 'skin-elite-miracle-balm',
        sourceId: form.id,
        itemCount: quantity,
        productId: root.dataset.productId,
      };

      form.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      }));

      let wasAdded = false;
      let rejected = false;

      try {
        const cart = await runCartMutation(async () => {
          const payload = await runCartRequest(async (signal) => {
            const response = await fetch(cartRoutes().add, {
              signal,
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [cartItem],
                sections: sections.slice(0, 5).join(','),
                sections_url: window.location.pathname,
              }),
            });
            const parsedPayload = await response.json().catch(() => ({}));
            if (!response.ok || parsedPayload.status) {
              rejected = response.status >= 400 && response.status < 500;
              throw new Error(parsedPayload.description || parsedPayload.message || 'This item could not be added.');
            }
            return parsedPayload;
          });

          wasAdded = true;
          return { ...await fetchCart(), sections: payload.sections };
        }, { sectionIds: sections });
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, sections: cart.sections, didError: false },
        });
        setStatus(`${quantity} ${quantity === 1 ? 'jar' : 'jars'} added to cart.${subscription.active ? ' Monthly delivery selected.' : ''} Shipping is calculated at checkout: $4.95 for Buy 1; free for the 4- and 6-jar bundles.`);

        if (!document.querySelector('cart-drawer-component[auto-open]')) {
          window.location.assign(cartRoutes().page);
        }
      } catch (error) {
        cartNeedsReview = true;
        const message = wasAdded
          ? 'Your item was added, but the cart display could not refresh. Check your cart before adding again.'
          : rejected
            ? `${error.message} Check your cart before adding again.`
            : 'We could not confirm the cart update. Check your cart before adding again.';

        form.dispatchEvent(new CartErrorEvent({
          error: message,
          code: rejected ? 'INVALID' : 'SERVICE_UNAVAILABLE',
          detail: { description: message },
        }));

        try {
          const cart = await fetchCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { ...detail, items: cart.items, didError: true },
          });
        } catch (refreshError) {
          deferred.reject(refreshError);
        }
        setStatus(message, true);
      } finally {
        busy = false;
        setButtonState();
      }
    });
  };

  const initClickFeedback = (root) => {
    let toast = root.querySelector('[data-preview-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ab-toast';
      toast.dataset.previewToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      root.appendChild(toast);
    }

    let toastTimer;
    const showToast = (message) => {
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 1800);
    };

    root.querySelectorAll('.ab-cta, .ab-thumb, .ab-bundle').forEach((element) => {
      element.addEventListener('click', () => {
        element.classList.remove('is-clicked');
        window.requestAnimationFrame(() => element.classList.add('is-clicked'));
        window.setTimeout(() => element.classList.remove('is-clicked'), 440);
      });
    });

    root.querySelectorAll('.ab-bundle').forEach((bundle) => {
      bundle.addEventListener('click', () => {
        const quantity = bundle.querySelector('[data-bundle-quantity]')?.dataset.bundleQuantity;
        if (quantity) showToast(`✓ ${quantity} jar${quantity === '1' ? '' : 's'} selected`);
      });
    });

    root.querySelectorAll('[data-primary-cta]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!button.closest('form[action*="/cart/add"]')) {
          showToast('Preview mode — Shopify cart activates after the theme is uploaded');
        }
      });
    });
  };

  const initStickyBar = (root) => {
    const primaryCta = root.querySelector('[data-primary-cta]');
    const stickyBar = root.querySelector('[data-sticky-bar]');
    if (!primaryCta || !stickyBar) return;

    const setVisibility = (isVisible) => {
      stickyBar.classList.toggle('is-visible', isVisible);
      stickyBar.setAttribute('aria-hidden', String(!isVisible));
    };

    const updateVisibility = () => setVisibility(primaryCta.getBoundingClientRect().bottom < 0);
    let visibilityFrame;
    const scheduleVisibilityUpdate = () => {
      if (visibilityFrame) return;
      visibilityFrame = window.requestAnimationFrame(() => {
        visibilityFrame = null;
        updateVisibility();
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        setVisibility(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.05 });
      observer.observe(primaryCta);
    }

    window.addEventListener('scroll', scheduleVisibilityUpdate, { passive: true });
    updateVisibility();
  };

  const initScrollButtons = (root) => {
    root.querySelectorAll('[data-scroll-to-offer]').forEach((button) => {
      button.addEventListener('click', () => {
        const stickyBar = root.querySelector('[data-sticky-bar]');
        stickyBar?.classList.remove('is-visible');
        stickyBar?.setAttribute('aria-hidden', 'true');
        root.querySelector('[data-bundle-list]')?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });
      });
    });
  };

  const initReveal = (root) => {
    const items = root.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item) => observer.observe(item));
  };

  const initStandardsMarquee = (root) => {
    const track = root.querySelector('.ab-standards-track');
    const sourceIcons = [...root.querySelectorAll('.ab-standards > .ab-standard > .ab-standard__icon')];
    if (!track || !sourceIcons.length || track.childElementCount) return;

    for (let copy = 0; copy < 2; copy += 1) {
      const group = document.createElement('div');
      group.className = 'ab-standards-group';
      if (copy === 1) group.setAttribute('aria-hidden', 'true');
      sourceIcons.forEach((icon) => {
        const item = document.createElement('span');
        item.className = 'ab-standards-mobile-item';
        item.appendChild(icon.cloneNode(true));
        group.appendChild(item);
      });
      track.appendChild(group);
    }
  };

  const initIngredients = (root) => {
    const card = root.querySelector('[data-ingredient-card]');
    if (!card) return;

    const image = card.querySelector('[data-ingredient-image]');
    const name = card.querySelector('[data-ingredient-name]');
    const job = card.querySelector('[data-ingredient-job]');
    const description = card.querySelector('[data-ingredient-description]');
    const tags = card.querySelector('[data-ingredient-tags]');
    const buttons = [...root.querySelectorAll('[data-ingredient-button]')];
    const orbit = root.querySelector('.ab-ingredient-orbit');
    const orbitButtons = [...root.querySelectorAll('.ab-ingredient-orbit [data-ingredient-button]')];
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    let selectedName = buttons.find((button) => button.classList.contains('is-active'))?.dataset.ingredientName || buttons[0]?.dataset.ingredientName || '';
    let imageTimer;

    const renderIngredient = (button) => {
      if (!button) return;
      if (name) name.textContent = button.dataset.ingredientName || '';
      if (job) job.textContent = button.dataset.ingredientJob || '';
      if (description) description.textContent = button.dataset.ingredientDescription || '';
      if (tags) {
        tags.replaceChildren(...(button.dataset.ingredientTags || '').split('|').filter(Boolean).map((label) => {
          const tag = document.createElement('span');
          tag.textContent = label;
          return tag;
        }));
      }

      if (image && button.dataset.ingredientImage) {
        window.clearTimeout(imageTimer);
        image.style.opacity = '0.18';
        image.style.transform = 'scale(1.1)';
        imageTimer = window.setTimeout(() => {
          image.src = button.dataset.ingredientImage;
          image.alt = button.dataset.ingredientName || '';
          image.style.opacity = '1';
          image.style.transform = '';
        }, 140);
      }
    };

    const selectIngredient = (button) => {
      selectedName = button.dataset.ingredientName || '';
      buttons.forEach((item) => {
        const isSelected = item.dataset.ingredientName === selectedName;
        item.classList.toggle('is-active', isSelected);
        item.setAttribute('aria-selected', String(isSelected));
      });
      renderIngredient(button);
    };

    const restoreSelection = () => {
      renderIngredient(buttons.find((button) => button.dataset.ingredientName === selectedName));
    };

    buttons.forEach((button) => button.addEventListener('click', () => selectIngredient(button)));
    orbitButtons.forEach((button) => {
      button.addEventListener('mouseenter', () => {
        if (hoverQuery.matches) renderIngredient(button);
      });
    });
    orbit?.addEventListener('mouseleave', () => {
      if (hoverQuery.matches) restoreSelection();
    });
  };

  const initProofGallery = (root) => {
    const gallery = root.querySelector('[data-proof-gallery]');
    const track = gallery?.querySelector('[data-proof-track]');
    const slides = gallery ? [...gallery.querySelectorAll('.ab-proof-slide')] : [];
    if (!gallery || !track || slides.length < 2) return;

    let activeIndex = 0;
    let autoplayTimer;

    const goToSlide = (index, behavior = 'smooth') => {
      activeIndex = (index + slides.length) % slides.length;
      track.scrollTo({ left: track.clientWidth * activeIndex, behavior });
      gallery.dataset.proofIndex = String(activeIndex);
    };

    const restartAutoplay = () => {
      window.clearInterval(autoplayTimer);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      autoplayTimer = window.setInterval(() => goToSlide(activeIndex + 1), 5000);
    };

    gallery.querySelector('[data-proof-prev]')?.addEventListener('click', () => {
      goToSlide(activeIndex - 1);
      restartAutoplay();
    });

    gallery.querySelector('[data-proof-next]')?.addEventListener('click', () => {
      goToSlide(activeIndex + 1);
      restartAutoplay();
    });

    let scrollTimer;
    track.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        activeIndex = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        gallery.dataset.proofIndex = String(activeIndex);
      }, 90);
    }, { passive: true });

    gallery.addEventListener('mouseenter', () => window.clearInterval(autoplayTimer));
    gallery.addEventListener('mouseleave', restartAutoplay);
    gallery.addEventListener('focusin', () => window.clearInterval(autoplayTimer));
    gallery.addEventListener('focusout', restartAutoplay);
    window.addEventListener('resize', () => goToSlide(activeIndex, 'auto'));

    gallery.dataset.proofIndex = '0';
    restartAutoplay();
  };

  const initCustomerReviews = (root) => {
    root.querySelectorAll('[data-customer-reviews]').forEach((section) => {
      const track = section.querySelector('[data-review-track]');
      const dots = [...section.querySelectorAll('[data-review-pagination] button')];
      const previous = section.querySelector('[data-review-previous]');
      const next = section.querySelector('[data-review-next]');
      if (!track || !dots.length || !previous || !next) return;

      let activeIndex = 0;
      let scrollTimer;

      const setActiveDot = (index) => {
        activeIndex = Math.max(0, Math.min(index, dots.length - 1));
        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === activeIndex;
          dot.classList.toggle('is-active', isActive);
          if (isActive) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const goToPage = (index) => {
        const wrappedIndex = (index + dots.length) % dots.length;
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const left = dots.length > 1 ? maxScroll * (wrappedIndex / (dots.length - 1)) : 0;
        setActiveDot(wrappedIndex);
        track.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };

      previous.addEventListener('click', () => goToPage(activeIndex - 1));
      next.addEventListener('click', () => goToPage(activeIndex + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => goToPage(index)));

      track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const maxScroll = Math.max(1, track.scrollWidth - track.clientWidth);
          setActiveDot(Math.round((track.scrollLeft / maxScroll) * (dots.length - 1)));
        }, 80);
      }, { passive: true });

      setActiveDot(0);
    });
  };

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    initThemeCompatibility();
    initGallery(root);
    initOffers(root);
    initCartDrawer(root);
    initStickyBar(root);
    initScrollButtons(root);
    initReveal(root);
    initStandardsMarquee(root);
    initIngredients(root);
    initProofGallery(root);
    initCustomerReviews(root);
    initClickFeedback(root);
  };

  document.querySelectorAll('[data-skin-elite-balm]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-skin-elite-balm]'));
  });
})();
